// #4401: ask Android to restart the whole app process.
//
// This exists because of one fact about Android that makes every other remedy useless
// there: in our own app the WebView keeps its storage machinery inside OUR process. So
// reloading the page, and recreating the activity, leave the jammed part exactly as it
// was. A customer sat through more than an hour of reloads that could not have worked.
//
// Killing our own process and letting the system start us again is the one rung available
// on Android that a browser page can never reach for itself, and it costs the user
// nothing — no data is touched and the app comes straight back.
//
// Everywhere else this is a no-op, and deliberately so. On the web there is no process to
// restart from inside the page; on iOS native the storage adapter is SQLite, whose open
// has been bounded since #3986 and which shows none of this failure's signature.

import { registerPlugin } from '@capacitor/core';
import { isCapacitorNative, isPlatformAndroid } from 'environments/environment-helpers';
import { ProcessRestartState, readProcessRestartState } from './process-restart-state';

interface AppRestartPluginInterface {
  /** Restart the process, once. Resolves `restarted:false` when the budget is spent. */
  restartForStorage(): Promise<{ restarted: boolean, reason?: string }>;
  /** Has the app already spent its restart budget? */
  storageRestartUsed(): Promise<{ used: boolean }>;
  /** Forget the count, because the app booted successfully. */
  clearStorageRestartCount(): Promise<void>;
}

const AppRestartPlugin = registerPlugin<AppRestartPluginInterface>('AppRestartPlugin');

/**
 * True only where a process restart is a thing the app can actually do (#4401).
 *
 * Android native only. On the web there is no process to restart from inside the page,
 * and on iOS native the storage adapter is SQLite, whose open has been bounded since
 * #3986 and which shows none of this failure's signature.
 */
function canRestartProcessForWedgedStore(): boolean {
  return isCapacitorNative() && isPlatformAndroid();
}

/**
 * What has happened to this app's one process restart (#4980)?
 *
 * Answers four ways rather than two, because the caller needs both to decide something and
 * to REPORT something, and those want different answers. `Available` is the only value that
 * means a restart is going to be attempted; `Spent` is the condition #4980 exists for — a
 * fresh process was already tried for this and the boot wedged again anyway.
 *
 * Never throws: it runs on a boot that is already failing, and a question that took the
 * failing path down with it would be worse than not asking. Every way of not knowing
 * answers `Unknown`, which is deliberately neither of the two real answers.
 */
export async function processRestartStateAsync(): Promise<ProcessRestartState> {
  const canRestart = canRestartProcessForWedgedStore();
  if (!canRestart) return readProcessRestartState(false, null);
  try {
    return readProcessRestartState(true, await AppRestartPlugin.storageRestartUsed());
  } catch {
    // not reported here: Unknown is carried by the local_storage_wedged report as
    // process_restart, which is the one place this answer is used
    return readProcessRestartState(true, null);
  }
}

/**
 * Restart the app process because the local store is not answering (#4401).
 *
 * Returns true only when a restart is actually under way — in which case nothing after
 * the call runs, because the process is about to die. False means "this is not something
 * I can do here", and the caller moves on to telling the user.
 *
 * Never throws: it is reached on a path that is already failing, and a rescue that took
 * the failing path down with it would be worse than no rescue.
 */
export async function restartProcessForWedgedStoreAsync(): Promise<boolean> {
  if (!canRestartProcessForWedgedStore()) return false;
  try {
    const result = await AppRestartPlugin.restartForStorage();
    return result?.restarted === true;
  } catch {
    // not reported here: false sends the caller straight to the local_storage_wedged report
    return false;
  }
}

/**
 * Forget that the app ever restarted itself, because a boot has now worked (#4401).
 *
 * The count means "restarts since the app last worked". Left to accumulate it would
 * eventually refuse a restart to somebody whose problem is brand new.
 */
export async function clearProcessRestartCountAsync(): Promise<void> {
  if (!canRestartProcessForWedgedStore()) return;
  try {
    await AppRestartPlugin.clearStorageRestartCount();
  } catch {
    // nothing useful to do — the worst case is one restart fewer for a device we could
    // not keep track of, which still ends at the honest error screen
  }
}
